// These are the pages you can go to.
// They are all wrapped in the App component, which should contain the navbar etc
// See http://blog.mxstbr.com/2016/01/react-apps-with-pages for more information
// about the code splitting business

import React, { useEffect, useRef, useState } from 'react';

import type { Store } from 'redux';
import type { Location } from 'history';

// If RouteConfig is not available, define a compatible type here
type RouteConfig = {
  path?: string;
  name?: string;
  component?: React.ComponentType<any>;
  indexRoute?: { component: React.ComponentType<any> };
  childRoutes?: RouteConfig[];
  onEnter?: (nextState: any, replace: any) => void;
  getComponent?: (...args: any[]) => void | Promise<void>;
};

// Utils
import { checkAuth, getAsyncInjectors } from 'app/utils/asyncInjectors';
import isAcquisitionSoftware from 'app/utils/isAcquisitionSoftware';
import { selectLocale } from 'app/redux/language/selectors';
import { getURLRedirectionWithNewLocale, isLanguageSupported } from 'app/utils/languageUtil';

// Containers
import App from 'app/containers/App';
import HomePage from 'app/containers/HomePage';
import DownloadPage from 'app/containers/DownloadPage';
import PartnerLinkPage from 'app/containers/PartnerLinkPage';
import ManagementPage from 'app/containers/ManagementPage';
import AccountPage from 'app/containers/AccountPage';
import SettingsPage from 'app/containers/SettingsPage';
import MonitoringPage from 'app/containers/MonitoringPage';
import ChangePasswordPage from 'app/containers/ChangePasswordPage';
import UnsubscribePage from 'app/containers/UnsubscribePage';
import ConfirmMailPage from 'app/containers/ConfirmMailPage';
import LoginPage from 'app/containers/LoginPage';
import PaymentSuccessPage from 'app/containers/PaymentSuccessPage';
import PaymentCanceledPage from 'app/containers/PaymentCanceledPage';
import AcquisitionPage from 'app/containers/AcquisitionPage';
import TutorialsPage from 'app/containers/TutorialsPage';
import PicoxIAAcquisitionPage from 'app/containers/PicoxIAAcquisitionPage';
import PicoxIAIntelligencePage from 'app/containers/PicoxIAIntelligencePage';
import PicoxiaTeleradiologyPage from 'app/containers/PicoxiaTeleradiologyPage';
import RequestDemoPage from 'app/containers/RequestDemoPage';
import AdminPage from 'app/containers/AdminPage';
import PrivacyPolicyPage from 'app/containers/PrivacyPolicyPage';
import LegalNoticesPage from 'app/containers/LegalNoticesPage';
import TermsOfSalesPage from 'app/containers/TermsOfSalesPage';
import TermsOfUsePage from 'app/containers/TermsOfUsePage';
import RegisterPage from 'app/containers/RegisterPage';
import CheatSheetPage from 'app/containers/CheatSheetPage';

// Components
import ProductionViewer from 'app/components/Viewer/ProductionViewer';
import PACSQuery from 'app/components/PACSQuery';
import TestDysplasiaPatternStructuredList from 'app/components/DysplasiaPatternStructuredList/TestDysplasiaPatternStructuredList';
import TestDysplasiaPattern from 'app/components/DysplasiaPatternStructuredList/TestDysplasiaPattern';
import TestPatientInfoForm from 'app/components/PatientInfoForm/TestPatientInfoForm';

const errorLoading = (err: unknown) => {
  // eslint-disable-next-line no-console
  console.error('Dynamic page loading failed', err);
};

const loadModule =
  (cb: (err: null, component: React.ComponentType<any>) => void) =>
  (componentModule: { default: React.ComponentType<any> }) => {
    cb(null, componentModule.default);
  };

interface NextState {
  location: Location;
  params: Record<string, string>;
}

type ReplaceFunction = (location: Partial<Location> | string) => void;

const redirectToLocaleIfNeeded = (
  store: Store<any>,
  { location }: NextState,
  replace: ReplaceFunction
) => {
  const urlLanguage = location.pathname.split('/')[1];
  const locale = selectLocale(store.getState());
  const firstParamIsASupportedLanguage = isLanguageSupported(urlLanguage);
  if (!firstParamIsASupportedLanguage) {
    replace(getURLRedirectionWithNewLocale(location, locale));
  }
};

interface ReloadableProductionViewerProps {
  location?: Location;
  [key: string]: any;
}

function ReloadableProductionViewer(props: ReloadableProductionViewerProps) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  const [key, setKey] = useState(() => require('uuid').v4());
  const mountCheckRef = useRef(false);

  useEffect(() => {
    if (!mountCheckRef.current) {
      mountCheckRef.current = true;
      return;
    }
    setKey(require('uuid').v4());
  }, [props.location]);
  return <ProductionViewer {...props} key={key} />;
}

const DEFAULT_PATH_FOR_DESKTOP = isAcquisitionSoftware() ? 'acquisition' : 'viewer';

export default function createRoutes(store: Store<any>): RouteConfig {
  // Create reusable async injectors using getAsyncInjectors factory
  const { injectReducer, injectSagas } = getAsyncInjectors(store);

  // const maintenance = [ ... ];
  const electronSpecific: RouteConfig[] = [
    {
      path: 'pacs-query',
      name: 'pacsQuery',
      component: PACSQuery,
    },
    {
      path: '*',
      onEnter: (nextState: NextState, replace: ReplaceFunction) =>
        replace({
          ...nextState.location,
          pathname: `/${nextState.params.lang}/${DEFAULT_PATH_FOR_DESKTOP}`,
        }),
    },
  ];

  const websiteSpecific: RouteConfig[] = [
    {
      path: 'download',
      name: 'downloadPage',
      component: DownloadPage,
    },
    {
      path: 'picoxia-acquisition',
      name: 'picoxIAAcquisition',
      component: PicoxIAAcquisitionPage,
    },
    {
      path: 'picoxia-intelligence',
      name: 'picoxIAIntelligence',
      component: PicoxIAIntelligencePage,
    },
    {
      path: 'picoxia-teleradiology',
      name: 'picoxiaTeleradiology',
      component: PicoxiaTeleradiologyPage,
    },
    {
      path: 'tutorials',
      name: 'tutorialsPage',
      component: TutorialsPage,
    },
    {
      path: 'request-demo',
      name: 'requestDemoPage',
      component: RequestDemoPage,
    },
    {
      path: 'link',
      name: 'partnerLinkPage',
      component: PartnerLinkPage,
    },
    {
      path: 'management',
      name: 'ManagementPage',
      onEnter: (nextState: NextState, replace: ReplaceFunction) =>
        checkAuth(store, nextState, replace),
      component: ManagementPage,
    },
    {
      path: 'admin',
      name: 'AdminPage',
      onEnter: (nextState: NextState, replace: ReplaceFunction) =>
        checkAuth(store, nextState, replace),
      component: AdminPage,
    },
    {
      name: 'homePage',
      // onEnter: (nextState, replace) => checkAuth(store, nextState, replace),
      component: HomePage,
    },
    {
      path: '*',
      onEnter: (nextState: NextState, replace: ReplaceFunction) =>
        replace({ ...nextState.location, pathname: `/${nextState.params.lang}/` }),
    },
  ];

  const common: RouteConfig[] = [
    {
      path: 'viewer',
      name: 'viewerPage',
      component: ReloadableProductionViewer,
    },
    // Redirection 301 de cheat_sheet vers cheat-sheet
    {
      path: 'cheat_sheet',
      onEnter: (_nextState: any, replace: any) => {
        replace({ pathname: '/cheat-sheet' });
      },
    },
    {
      path: 'cheat-sheet',
      name: 'CheatSheetPage',
      component: CheatSheetPage,
    },
    {
      path: 'account',
      name: 'accountPage',
      onEnter: (nextState: NextState, replace: ReplaceFunction) =>
        checkAuth(store, nextState, replace),
      component: AccountPage,
    },
    {
      path: 'settings',
      name: 'settingsPage',
      onEnter: (nextState: NextState, replace: ReplaceFunction) =>
        checkAuth(store, nextState, replace),
      component: SettingsPage,
    },
    {
      path: 'monitoring',
      name: 'monitoringPage',
      onEnter: (nextState: NextState, replace: ReplaceFunction) =>
        checkAuth(store, nextState, replace),
      component: MonitoringPage,
    },
    {
      path: 'change_password',
      name: 'changePasswordPage',
      component: ChangePasswordPage,
    },
    {
      path: 'unsubscribe_mail',
      name: 'UnsubscribePage',
      component: UnsubscribePage,
    },
    {
      path: 'confirm_email',
      name: 'confirmMailPage',
      component: ConfirmMailPage,
    },
    {
      path: 'login',
      name: 'loginPage',
      component: LoginPage,
    },
    {
      path: 'purchase',
      name: 'billingPage',
      async getComponent(
        nextState: NextState,
        cb: (err: null, component: React.ComponentType<any>) => void
      ) {
        const BillingPage = (await import('app/containers/BillingPage')).default;
        cb(null, BillingPage);
      },
    },
    {
      path: 'purchase_success',
      name: 'paymentSuccess',
      component: PaymentSuccessPage,
    },
    {
      path: 'purchase_canceled',
      name: 'paymentCanceledPage',
      component: PaymentCanceledPage,
    },
    {
      path: 'register',
      name: 'registerPage',
      component: RegisterPage,
    },
    {
      path: 'acquisition',
      name: 'AcquisitionPage',
      component: AcquisitionPage,
    },
    {
      path: 'privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
    },
    {
      path: 'legal-notices',
      name: 'LegalNoticesPage',
      component: LegalNoticesPage,
    },
    {
      path: 'terms-of-sales',
      name: 'TermsOfSalesPage',
      component: TermsOfSalesPage,
    },
    {
      path: 'terms-of-use',
      name: 'TermsOfUsePage',
      component: TermsOfUsePage,
    },
  ];

  const testRoutes: RouteConfig[] = [
    {
      path: 'test_dysplasia_pattern_structured_list',
      name: 'TestDysplasiaPatternStructuredList',
      component: TestDysplasiaPatternStructuredList,
    },
    {
      path: 'test_dysplasia_pattern',
      name: 'TestDysplasiaPattern',
      component: TestDysplasiaPattern,
    },
    {
      path: 'test_patient_info_form',
      name: 'TestPatientInfoForm',
      component: TestPatientInfoForm,
    },
  ];

  let childRoutes: RouteConfig[] = common;
  if (process.env.NODE_ENV === 'development') {
    childRoutes = childRoutes.concat(testRoutes);
  }
  if (process.env.PLATFORM === 'electron') {
    childRoutes = childRoutes.concat(electronSpecific);
  } else {
    childRoutes = childRoutes.concat(websiteSpecific);
  }

  let componentToFirstLoad: React.ComponentType<any>;
  if (process.env.PLATFORM === 'electron') {
    if (isAcquisitionSoftware()) {
      componentToFirstLoad = AcquisitionPage;
    } else {
      componentToFirstLoad = ReloadableProductionViewer;
    }
  } else {
    componentToFirstLoad = HomePage;
  }

  // Wrapp all Routes in the App component
  const rootRoute: RouteConfig = {
    component: App,
    path: '/',
    indexRoute: { component: componentToFirstLoad },
    childRoutes: [
      {
        indexRoute: { component: componentToFirstLoad },
        onEnter: (nextState: NextState, replace: ReplaceFunction) =>
          redirectToLocaleIfNeeded(store, nextState, replace),
        path: ':lang',
        childRoutes,
      },
    ],
  };

  return rootRoute;
}
